// src/pages/AuditRetentionPage.tsx
//
// ★★★ WAVE 13 (P10) — P2C1 §12.141's RETENTION-STANDARD LIMB, on a screen.
//
// ══ THE GAP THIS CLOSES ══
//
// §12.141 asks for *"storage/access/management/archival **per the Authority's
// retention standards**"*. The protection limbs were built in
// `009_ledger_immutability.sql`; the archival limb is §12.134 and has had a
// screen since Wave 9. The retention STANDARD had nowhere to live, nothing to
// administer it, and nothing to report against it — the RTM's *"not built: the
// archival and retention-standard limb of the same clause"*.
//
// ══ ⚠ THE TWO SENTENCES THIS SCREEN EXISTS TO KEEP TRUE ══
//
//   1. **No retention policy is in force.** The pack furnishes no standard, so
//      nothing is seeded and a period authored here is provisional. The standing
//      sentence is the SERVER's (`assessment.standing`) — a `.tsx` copy would go
//      on asserting it after a policy was authored.
//   2. **Nothing in this product disposes of anything.** Every subject row
//      renders its own `disposal` posture, and the three append-only classes
//      render the stronger one: disposal is IMPOSSIBLE, because the database
//      refuses DELETE from every role including a superuser.
//
// ⚠ **`pastRetention: null` is rendered as "Not applicable", never as 0.** Zero
// would claim nothing is past retention, which is a statement about an interval
// nobody has defined. That is the not-built/no-data distinction this product has
// a standing rule about, on a compliance surface where it decides whether an
// auditor thinks the estate is compliant or unmeasured.
import { plainCode, plainProse } from '../lib/plain-words'
import { CategoryBadge } from '../components/StatusBadge'
import { IconButton } from '../components/IconButton'
import { useState, type FormEvent } from 'react'
import { SgdsAlert, SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { FormResult } from '../components/FormResult'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { boundsProblems } from '../lib/published-bounds'
import { wholeNumberProblem } from '../components/FieldError'
import { targetValue } from '../lib/sgds-dom'
import { formatInstant } from '../lib/format'
import type { RetentionSubjectAssessment } from '../lib/types'

const BOUNDED_FIELDS = {
  retentionMonths: 'Retention period (whole months)',
  standardReference: 'The cited standard',
  note: 'Note (optional)',
} as const

/** A withdrawal sends none of these, so none of them can be in breach. */
const ESTABLISH_ONLY_FIELDS = ['retentionMonths', 'standardReference'] as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

/** The short explanation shown beside the disposition badge. */
const DISPOSITION_NOTE: Readonly<Record<string, string>> = {
  RETAIN_INDEFINITELY: 'no period applies',
  ARCHIVE_THEN_RETAIN: 'in sealed segments',
  REVIEW_AT_EXPIRY: 'a person decides',
  DISPOSE_AT_EXPIRY: 'recorded, never performed by this system',
}

/** Human wording for each closed disposition value. The VALUE is what is stored. */
const DISPOSITION_LABEL: Readonly<Record<string, string>> = {
  RETAIN_INDEFINITELY: 'Retain indefinitely: no period applies',
  ARCHIVE_THEN_RETAIN: 'Archive then retain (in sealed segments)',
  REVIEW_AT_EXPIRY: 'Review at expiry: a person decides',
  DISPOSE_AT_EXPIRY: 'Dispose at expiry: ⚠ recorded, never performed by PRS',
}

/**
 * ★ ONE SUBJECT'S DISPOSAL POSTURE, and the three answers are deliberately not
 * one answer.
 *
 * ⚠ *Not performed* and *cannot be performed* are different answers to a
 * compliance question. Collapsing them would let a reader conclude either that
 * PRS quietly deletes audit rows or that a `DISPOSE_AT_EXPIRY` policy over
 * `app.bill` is being honoured. Neither is true, and they are untrue for
 * different reasons.
 */
function DisposalPosture({ row }: { row: RetentionSubjectAssessment }) {
  const disposal = row.disposal
  if (disposal.kind === 'IMPOSSIBLE_BY_CONSTRUCTION') {
    return (
      <>
        <p className="text-sm font-semibold">Disposal is impossible for this class, by construction.</p>
        <p className="text-sm mt-1 text-[var(--sgds-body-color-subtle)]">{plainProse(disposal.reason)}</p>
      </>
    )
  }
  if (disposal.kind === 'DUE_BUT_NOT_PERFORMED') {
    return (
      <>
        <p className="text-sm font-semibold">
          The policy asks for disposal at expiry. <strong>PRS performs none.</strong>
        </p>
        <p className="text-sm mt-1 text-[var(--sgds-body-color-subtle)]">{plainProse(disposal.reason)}</p>
      </>
    )
  }
  if (disposal.kind === 'NOTHING_DUE_BY_POLICY') {
    return (
      <p className="text-sm">
        Nothing is due for disposal: the policy in force is{' '}
        <strong>{DISPOSITION_LABEL[disposal.disposition] ?? disposal.disposition}</strong>.
      </p>
    )
  }
  return (
    <p className="text-sm text-[var(--sgds-body-color-subtle)]">
      No policy is in force for this class, so nothing is due and nothing is claimed.
    </p>
  )
}

export function AuditRetentionPage() {
  const retention = useApi(() => api.retention(), [])

  const [subject, setSubject] = useState('AUDIT_EVENT')
  const [disposition, setDisposition] = useState('ARCHIVE_THEN_RETAIN')
  const [months, setMonths] = useState('')
  const [standardReference, setStandardReference] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [withdrawInstead, setWithdrawInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  const assessment = retention.data?.assessment
  const validation = retention.data?.validation
  const indefinite = disposition === 'RETAIN_INDEFINITELY'

  /** ⚠ Shape only. The bound is the route's and is reported from `validation`. */
  const monthsTypeProblem = wholeNumberProblem(months, BOUNDED_FIELDS.retentionMonths)

  const problems = boundsProblems(
    validation,
    { retentionMonths: months.trim(), standardReference: standardReference.trim(), note: note.trim() },
    BOUNDED_FIELDS,
    {
      // ⚠ A withdrawal sends neither, and RETAIN_INDEFINITELY sends no period —
      // so neither can be in breach in those states. Without naming them, a
      // half-typed period left in state disables Save with no error on screen:
      // the Wave 5 failure `FieldError.tsx` records.
      notSubmitted: withdrawInstead
        ? ESTABLISH_ONLY_FIELDS
        : indefinite
          ? (['retentionMonths'] as const)
          : [],
    },
  )
  const monthsProblem = indefinite || withdrawInstead ? null : (monthsTypeProblem ?? problems.of.retentionMonths)

  const canSubmit =
    effectiveFrom.length > 0 &&
    !problems.any &&
    (withdrawInstead || indefinite || (months.trim().length > 0 && monthsProblem === null))

  /**
   * ★★ WAVE 26 — Record FREEZES a sentence and asks; the write happens only from
   * the confirmation (`ConfirmWrite`'s header says why every irreversible write
   * does this now). Composed once, from the fields as they were when the button
   * was pressed — never re-derived from a form the officer may since have edited.
   *
   * ⚠ The sentence names the record class, the period, the date — and that PRS
   * disposes of nothing, because that is the one fact about a retention policy an
   * operator is most likely to assume the other way (this file's header, item 2).
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const cited = standardReference.trim()
    const period = indefinite ? 'no retention period: retain indefinitely' : `a retention period of ${months.trim()} month(s)`
    setConfirming(
      withdrawInstead
        ? `Withdraw the retention policy in force for ${plainCode(subject)} from ${effectiveFrom}. From that date no retention standard applies to that record class, and its overdue count reports as not applicable rather than zero. PRS disposes of nothing either way. There is no undo. A mistake is corrected by another version.`
        : `Record for ${plainCode(subject)}, from ${effectiveFrom}: ${period}, then "${DISPOSITION_LABEL[disposition] ?? disposition}"${cited.length > 0 ? `, citing ${cited}` : ', with no standard cited'}. This supersedes whatever policy is in force for that record class from that date. PRS disposes of nothing: the register records the standard and measures the gap against it. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeRetentionPolicy(subject, {
        effectiveFrom,
        establish: !withdrawInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
        ...(!withdrawInstead && {
          disposition,
          retentionMonths: indefinite ? null : Number(months),
          ...(standardReference.trim().length > 0 && { standardReference: standardReference.trim() }),
        }),
      })) as { outcome: string; consequence?: string }
      setForm({
        status: 'success',
        // ⚠ The SERVER's own sentence where it gives one. Both the "recorded, not
        // performed" and the "no standard in force" consequences are claims about
        // what the system does, and this screen must not restate them in its own
        // words.
        message: outcome.consequence ?? `${plainCode(subject)}: policy ${outcome.outcome.toLowerCase()} from ${effectiveFrom}.`,
      })
      setMonths('')
      setStandardReference('')
      setEffectiveFrom('')
      setNote('')
      retention.reload()
    } catch (error) {
      setForm({
        status: 'error',
        message: error instanceof ApiError ? error.message : 'Could not record this retention policy.',
      })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Retention standard</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
          What PSD requires be kept, for how long, and what happens at expiry. The other parts of this requirement are
          elsewhere: the trail is protected from modification by every role including a superuser (enforced at the
          database), and archival is on the <strong>Audit archive</strong> screen.
        </p>
      </div>

      {retention.error !== null ? (
        <ErrorCard title="Could not read the retention register" message={retention.error} onRetry={retention.reload} />
      ) : retention.initialLoading ? (
        <LoadingCard label="Reading the retention register" rows={4} />
      ) : assessment === undefined ? (
        // ★ WAVE 26 — this branch rendered NOTHING: a 200 with no assessment left the
        // heading and then an empty page, indistinguishable from a screen that broke.
        <EmptyCard
          title="No retention assessment was returned"
          message="The register answered without an assessment, so nothing is due and nothing is claimed. Reload to ask again."
        />
      ) : (
        <>
          {/*
            ⚠ THE STANDING SENTENCE, and it is the SERVER's. It changes on its own
            when a policy is authored, because it is a claim about the database —
            a `.tsx` copy would keep telling an officer no standard exists the day
            after they recorded one.
          */}
          <SgdsAlert show variant="info" title="What this register says today">
            <p className="text-sm">{plainProse(assessment.standing)}</p>
          </SgdsAlert>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-[var(--sgds-body-color-subtle)]">
              Assessed at {formatInstant(assessment.assessedAt)}. {assessment.policiesInForce} of{' '}
              {assessment.subjects.length} record class(es) have a policy in force.
            </p>
            <IconButton glyph="refresh" label="Refresh" onClick={retention.reload} />
          </div>

          <div className="flex flex-col gap-4">
            {assessment.subjects.map((row) => (
              <div key={row.subject} className="pt-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">
                      <span title={row.subject}>{plainCode(row.subject)}</span>
                    </h2>
                    <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">{plainProse(row.describes)}</p>
                  </div>
                  {row.immutable && (
                    <span className="pt-chip pt-chip-navy" title="The database refuses DELETE and TRUNCATE from every role, including a superuser.">
                      Append-only
                    </span>
                  )}
                </div>

                <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 max-w-2xl">
                  <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Records held</dt>
                  <dd className="pt-tabular">{row.rowCount.toLocaleString('en-SG')}</dd>
                  <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Oldest record</dt>
                  <dd className="text-sm">{row.oldestAt === null ? 'None: this class is empty' : formatInstant(row.oldestAt)}</dd>
                  <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Policy in force</dt>
                  <dd className="text-sm">
                    {row.policy === null ? (
                      <span className="text-[var(--sgds-body-color-subtle)]">
                        None. The pack states no retention standard for this class.
                      </span>
                    ) : (
                      <>
                        <CategoryBadge kind="disposition" value={row.policy.disposition} label={plainCode(row.policy.disposition)} />{' '}
                        {DISPOSITION_NOTE[row.policy.disposition] !== undefined && `(${DISPOSITION_NOTE[row.policy.disposition]})`}
                        {row.policy.retentionMonths !== null && ` after ${row.policy.retentionMonths} month(s)`}, from{' '}
                        {row.policy.effective.from}.{' '}
                        {row.policy.standardReference === null ? (
                          <strong>No standard cited.</strong>
                        ) : (
                          <>Standard: {plainProse(row.policy.standardReference)}.</>
                        )}
                        {/*
                          ★★★ WAVE 14 — THE PROVENANCE NOTE, RENDERED.
                          ⚠ The row already carried `note` from the repository through
                          the route to this type, and no surface showed it. That is the
                          exact shape of the ward-rate failure recorded in
                          seed-demonstration-policy.mjs's header: twelve fabricated
                          subsidy rates moved money on screen for weeks while the only
                          place saying they were fabricated was a column nothing
                          rendered. A demonstration retention standard is invented
                          records-management policy — it must say so where it is read.
                        */}
                        {row.policy.note !== null && (
                          <span className="block text-xs text-[var(--sgds-body-color-subtle)]">{plainProse(row.policy.note)}</span>
                        )}
                      </>
                    )}
                  </dd>
                  <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Past retention</dt>
                  <dd className="pt-tabular">
                    {/*
                      ⚠ "Not applicable", NEVER 0. Zero would claim nothing is past
                      retention — a statement about an interval nobody has defined.
                    */}
                    {row.pastRetention === null ? (
                      <span className="text-[var(--sgds-body-color-subtle)]">
                        Not applicable: no period is in force to be past
                      </span>
                    ) : (
                      row.pastRetention.toLocaleString('en-SG')
                    )}
                  </dd>
                </dl>

                <div className="pt-well p-3 mt-4">
                  <DisposalPosture row={row} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/*
        ⚠ **THE FORM IS NOT RENDERED UNTIL THE REGISTER HAS BEEN READ, and that is
        two things at once.**

        The first is correctness for the officer: a caller whose read was REFUSED
        (403 — this register is reserved to a full-audit-trail role) must not be
        shown an authoring form they cannot submit. The error card above is the
        whole answer in that state.

        The second is a real defect this closes. Both selects below take their
        options from the PAYLOAD — the subjects and the dispositions are the
        server's closed domains, not this file's, for the reason
        `administration-field-bounds.ts` gives about bounds. Rendered before the
        payload arrives they hold a `value` matching no option, and
        `sgds-select`'s own `_updateDisplayValue` throws
        `Cannot read properties of undefined (reading 'label')` on the slot-change
        tick. It surfaced as 48 unhandled rejections in the suite — passing tests
        beside a component erroring on every mount, which is exactly the state
        Vitest warns "might cause false positive tests". The alternative fix —
        hard-coding the two domains here as a fallback — is the S-011 second copy
        this whole screen avoids elsewhere.
      */}
      {retention.data !== undefined && (
      <form className="pt-card pt-no-print" onSubmit={handleSubmit}>
        <h2 className="text-lg font-semibold mb-1">Record a retention standard</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch] mb-4">
          A new policy supersedes whatever is in force for this record class from the date you give. It cannot be
          back-dated over a period it was not in force for: what was kept, and for how long, is a fact about the past.
        </p>

        <div className="grid gap-4 md:grid-cols-2 max-w-3xl">
          <SgdsSelect label="Record class" value={subject} onSgdsChange={(event) => setSubject(targetValue(event))}>
            {(retention.data?.subjects ?? []).map((value) => (
              <SgdsSelectOption key={value} value={value}>
                {plainCode(value)}
              </SgdsSelectOption>
            ))}
          </SgdsSelect>

          {!withdrawInstead && (
            <SgdsSelect
              label="At expiry"
              value={disposition}
              onSgdsChange={(event) => setDisposition(targetValue(event))}
            >
              {(retention.data?.dispositions ?? []).map((value) => (
                <SgdsSelectOption key={value} value={value}>
                  {DISPOSITION_LABEL[value] ?? value}
                </SgdsSelectOption>
              ))}
            </SgdsSelect>
          )}

          {!withdrawInstead && !indefinite && (
            <BoundedField
              label={BOUNDED_FIELDS.retentionMonths}
              value={months}
              onValueChange={setMonths}
              bounds={validation?.retentionMonths}
              problem={monthsProblem}
              required
              inputMode="numeric"
              example="e.g. 84 for seven years."
            />
          )}

          <label className="flex flex-col gap-1">
            <span className="text-sm font-semibold">Effective from</span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
        </div>

        {!withdrawInstead && (
          <div className="mt-4 max-w-3xl">
            <BoundedField
              label={BOUNDED_FIELDS.standardReference}
              value={standardReference}
              onValueChange={setStandardReference}
              bounds={validation?.standardReference}
              problem={problems.of.standardReference}
              example="Name the document this period comes from. Left blank, the register reports that none was cited."
            />
          </div>
        )}

        <div className="mt-4 max-w-3xl">
          <BoundedField
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
            rows={2}
          />
        </div>

        {/*
          ⚠ THE WARNING THAT MUST BE ON THE CONTROL, not only in the response. An
          operator choosing "Dispose at expiry" is making a records-management
          decision, and the single most surprising fact about it is that PRS will
          not carry it out.
        */}
        {!withdrawInstead && disposition === 'DISPOSE_AT_EXPIRY' && (
          <div className="mt-4 max-w-3xl">
            <SgdsAlert show variant="warning" title="Recorded, and not performed">
              <p className="text-sm">
                <strong>PRS disposes of nothing.</strong> No code path in this product deletes a record of any class,
                on a schedule or otherwise. Recording this policy states what PSD requires; the assessment
                above will then report how many records are past it, as a measurement of the gap and never as work
                done. For the audit trail entries the register refuses this choice
                outright: the trail refuses DELETE from every role including a superuser.
              </p>
            </SgdsAlert>
          </div>
        )}

        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={withdrawInstead}
            onChange={(event) => {
              setWithdrawInstead(event.target.checked)
            }}
          />
          <span>
            Withdraw the policy in force for this record class instead. From that date there is{' '}
            <strong>no retention standard</strong> for it, and the assessment reports its overdue count as not
            applicable rather than as zero.
          </span>
        </label>

        <div className="mt-4">
          <SgdsButton variant="primary" type="submit" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
            {withdrawInstead ? 'Withdraw this policy' : 'Record this policy'}
          </SgdsButton>
        </div>

        {confirming !== null && (
          <div className="mt-4 max-w-3xl">
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          </div>
        )}
        <FormResult state={form} />
      </form>
      )}
    </div>
  )
}
